import {
  isDashboardDeceasedDescendantMember,
  isDashboardDeceasedInLawMember,
  isDashboardDeceasedMember,
  isDashboardDeceasedRelatedMember,
  isDashboardDescendantMember,
  isDashboardInRelationshipInLawMember,
  isDashboardLivingDescendantMember,
  isDashboardLivingInLawMember,
  isDashboardLivingMember,
  isDashboardLivingRelatedMember,
  isDashboardRelatedMember,
} from "./dashboardMemberDisplayName";
import { isDashboardStartOrTopOfLineMember } from "./dashboardStartOfLineMember";

/**
 * Canonical member-category filter keys used on dashboard detail pages.
 *
 * The three Related keys are the Descendants trio with Start/Top of Line folded
 * in — Family Members shows those in place of the Descendants tabs, so the head
 * of each line is counted with the blood family rather than on a tab of its own.
 */
export const DASHBOARD_MEMBER_FILTERS = {
  ALL: "all",
  START_TOP: "start-top",
  DESCENDANTS: "descendants",
  DESCENDANTS_LIVING: "descendants-living",
  DESCENDANTS_DECEASED: "descendants-deceased",
  RELATED: "related",
  RELATED_LIVING: "related-living",
  RELATED_DECEASED: "related-deceased",
  IN_LAWS: "in-laws",
  IN_LAWS_DECEASED: "in-laws-deceased",
  IN_RELATIONSHIP: "in-relationship",
  LIVING: "living",
  DECEASED: "deceased",
};

/**
 * The member tabs a report carries — the set every report page starts from:
 *
 *   Start/Top of Line | Descendants | Living | Deceased
 *
 * A report lists blood family and the couple at the head of each line, never
 * in-laws, so the three in-law tabs are the ones it withholds. The two split
 * descendant tabs go with them: Living and Deceased already read the same two
 * lists on a page whose rows are all descendants, so the pair only repeated
 * what the status tabs beside them do. Everything else on the bar — the sorts,
 * the divider, the pairing rules — is the shared bar's own, unchanged.
 */
export const DASHBOARD_REPORT_EXCLUDED_MEMBER_FILTERS = [
  DASHBOARD_MEMBER_FILTERS.DESCENDANTS_LIVING,
  DASHBOARD_MEMBER_FILTERS.DESCENDANTS_DECEASED,
  DASHBOARD_MEMBER_FILTERS.IN_LAWS,
  DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED,
  DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP,
];

/**
 * Spread onto DashboardDetailSortFilterBar to give a report the tab set above.
 * A new report page wires its bar with this rather than restating which tabs it
 * drops, so every report keeps the same tabs in the same order:
 *
 *   <DashboardDetailSortFilterBar
 *     {...DASHBOARD_REPORT_MEMBER_FILTER_BAR_PROPS}
 *     sortBy={sortBy}
 *     onSortChange={setSortBy}
 *     memberFilter={memberFilter}
 *     onMemberFilterToggle={toggleMemberFilter}
 *   />
 */
export const DASHBOARD_REPORT_MEMBER_FILTER_BAR_PROPS = {
  excludedMemberFilters: DASHBOARD_REPORT_EXCLUDED_MEMBER_FILTERS,
  showMemberFilters: true,
  showLivingDeceasedFilters: true,
};

/**
 * Filter a member list by the shared dashboard member-category tab.
 * @param {Array} members
 * @param {string} memberFilter
 */
export function filterDashboardMembersByMemberFilter(members, memberFilter) {
  const list = Array.isArray(members) ? members : [];
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.IN_LAWS) {
    return list.filter((m) => isDashboardLivingInLawMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.DESCENDANTS) {
    return list.filter((m) => isDashboardDescendantMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.DESCENDANTS_LIVING) {
    return list.filter((m) => isDashboardLivingDescendantMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.DESCENDANTS_DECEASED) {
    return list.filter((m) => isDashboardDeceasedDescendantMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.RELATED) {
    return list.filter((m) => isDashboardRelatedMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.RELATED_LIVING) {
    return list.filter((m) => isDashboardLivingRelatedMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.RELATED_DECEASED) {
    return list.filter((m) => isDashboardDeceasedRelatedMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED) {
    return list.filter((m) => isDashboardDeceasedInLawMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP) {
    return list.filter((m) => isDashboardInRelationshipInLawMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.START_TOP) {
    return list.filter((m) => isDashboardStartOrTopOfLineMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.LIVING) {
    return list.filter((m) => isDashboardLivingMember(m));
  }
  if (memberFilter === DASHBOARD_MEMBER_FILTERS.DECEASED) {
    return list.filter((m) => isDashboardDeceasedMember(m));
  }
  return list;
}

/**
 * The sorts each member tab puts out of reach on the left of the bar.
 *
 * Two reasons a sort is withheld: the tab already lands in that order, so the
 * button would do nothing (Descendants read oldest first, the two in-law tabs
 * read A–Z), or the tab is a list the sort does not apply to — deceased lists
 * are read by death year, and in-laws are not counted in anything involving age.
 *
 * Start/Top of Line withholds the whole left side: the heads of the lines are
 * read in one fixed order (oldest first, the order they start their lines in),
 * so every sort is grayed out and the list keeps that default.
 */
const DASHBOARD_ALL_LEFT_SIDE_SORTS = [
  "alpha-asc",
  "alpha-desc",
  "color",
  "oldest",
  "youngest",
];

const DASHBOARD_DISABLED_SORTS_BY_MEMBER_FILTER = {
  [DASHBOARD_MEMBER_FILTERS.START_TOP]: DASHBOARD_ALL_LEFT_SIDE_SORTS,
  [DASHBOARD_MEMBER_FILTERS.DESCENDANTS]: ["oldest"],
  [DASHBOARD_MEMBER_FILTERS.DESCENDANTS_LIVING]: ["oldest"],
  [DASHBOARD_MEMBER_FILTERS.DESCENDANTS_DECEASED]: ["oldest", "youngest"],
  // Related reads the same way as Descendants: oldest first, deceased by death year.
  [DASHBOARD_MEMBER_FILTERS.RELATED]: ["oldest"],
  [DASHBOARD_MEMBER_FILTERS.RELATED_LIVING]: ["oldest"],
  [DASHBOARD_MEMBER_FILTERS.RELATED_DECEASED]: ["oldest", "youngest"],
  [DASHBOARD_MEMBER_FILTERS.IN_LAWS]: ["alpha-asc", "oldest", "youngest"],
  [DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED]: ["oldest", "youngest"],
  [DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP]: ["oldest", "youngest", "alpha-asc"],
};

/**
 * Which left-side sort each right-side member tab can be paired with.
 *
 * @param {string} sortValue — a `sortBy` value, e.g. "alpha-asc" / "oldest"
 * @param {string} memberFilter — one of DASHBOARD_MEMBER_FILTERS
 */
export function isDashboardSortAllowedForMemberFilter(sortValue, memberFilter) {
  const disabled = DASHBOARD_DISABLED_SORTS_BY_MEMBER_FILTER[memberFilter];
  return !disabled || !disabled.includes(sortValue);
}

/**
 * The order a member tab lands in on its own, with nothing lit on the sort side.
 *
 * Picking a tab on the right of the divider clears the left side, so the bar
 * only ever shows one of the two sides selected. Pages read this to keep
 * ordering the list the way its tab is meant to be read while no sort button
 * is lit for it.
 *
 * @param {string} memberFilter — one of DASHBOARD_MEMBER_FILTERS
 * @param {string} [fallback="none"] — the order for tabs that have no order of
 *   their own; pages with a base order (Birthdays/Memorials read by Date, Heads
 *   of Line by age) pass theirs here
 * @returns {string} a `sortBy` value to order by — never lit on the bar
 */
export function getDashboardBaseSortForMemberFilter(
  memberFilter,
  fallback = "none"
) {
  switch (memberFilter) {
    // The deceased lists are read by year of death, so the members who died in
    // the same year sit together.
    case DASHBOARD_MEMBER_FILTERS.DESCENDANTS_DECEASED:
    case DASHBOARD_MEMBER_FILTERS.RELATED_DECEASED:
    case DASHBOARD_MEMBER_FILTERS.IN_LAWS_DECEASED:
      return "death-asc";
    // The in-law lists read as plain name lists, so A–Z is the order they land
    // in — which is also why the bar grays A–Z out for them.
    case DASHBOARD_MEMBER_FILTERS.IN_LAWS:
    case DASHBOARD_MEMBER_FILTERS.IN_RELATIONSHIP:
      return "alpha-asc";
    case DASHBOARD_MEMBER_FILTERS.DESCENDANTS:
    case DASHBOARD_MEMBER_FILTERS.DESCENDANTS_LIVING:
    case DASHBOARD_MEMBER_FILTERS.RELATED:
    case DASHBOARD_MEMBER_FILTERS.RELATED_LIVING:
    case DASHBOARD_MEMBER_FILTERS.START_TOP:
      return "oldest";
    default:
      return fallback;
  }
}

/**
 * Toggle a member filter (the same tab again clears back to "all") and clear
 * the sort side of the bar.
 *
 * The two sides of the divider never read as selected together: a tab picked on
 * the right is selected on its own, and the sorts on the left all go unlit. The
 * list still lands in the order the tab is read in — pages resolve that through
 * getDashboardBaseSortForMemberFilter, which lights no button. Clicking a sort
 * afterwards lights it and orders the tab's list by it.
 *
 * @param {string} currentFilter
 * @param {string} nextFilter
 * @param {(sort: string) => void} [setSortBy] — called with "none" to unlight
 *   the sort side
 */
export function applyDashboardMemberFilterToggle(
  currentFilter,
  nextFilter,
  setSortBy
) {
  const next = currentFilter === nextFilter ? DASHBOARD_MEMBER_FILTERS.ALL : nextFilter;
  if (typeof setSortBy === "function") {
    setSortBy("none");
  }
  return next;
}
